import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, Clock, Loader2 } from 'lucide-react';
import { friendlyError, getCheckoutSession } from '@/lib/api';

// Clave compartida con CheckoutSession.jsx. Se usa SOLO para reconstruir el
// contexto de retorno (Redsys UrlOK/UrlKO son estáticas y no conservan `next`).
// Se persiste en sessionStorage (no localStorage) para que sea:
//   - temporal (dura solo mientras la pestaña esté abierta);
//   - pertenezca a la pestaña que inició el checkout, evitando que dos
//     compras simultáneas en pestañas distintas se pisen la clave global;
//   - sobreviva a la navegación Redsys → NetScouters (misma pestaña, mismo
//     origen);
//   - se elimine automáticamente al cerrar la pestaña.
// El acceso al contenido lo decide siempre el backend cuando el webhook
// procesa la operación; esta pantalla nunca concede acceso por el hecho de
// haber sido cargada.
const CHECKOUT_RETURN_STORAGE_KEY = 'ns_pending_checkout_return';
// Polling de estado: cada 1500 ms, hasta ~15 s. Suficiente para dar tiempo
// al webhook server-to-server sin dejar al usuario colgado.
const POLL_INTERVAL_MS = 1500;
const POLL_MAX_MS = 15000;
// Válido durante 1 hora para descartar restos antiguos en sessionStorage.
const RETURN_CONTEXT_TTL_MS = 60 * 60 * 1000;

/**
 * `sanitizeInternalNext` se consolida en `@/lib/safeNext` para que Login,
 * Register y este checkout usen exactamente la misma validación anti
 * open-redirect sin duplicar lógica.
 */
import { sanitizeInternalNext } from '@/lib/safeNext';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

/**
 * Lee el contexto persistido durante el checkout. Devuelve null si:
 *  - no hay entrada válida;
 *  - la entrada está caducada (TTL 1 h);
 *  - `expectedSessionId` no coincide EXACTAMENTE con el session_id guardado
 *    (evita cross-session contamination si el usuario abrió varias compras).
 */
function readCheckoutReturnContext(expectedSessionId) {
  try {
    const raw = window.sessionStorage.getItem(CHECKOUT_RETURN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.session_id) return null;
    if (typeof parsed.ts === 'number' && (Date.now() - parsed.ts) > RETURN_CONTEXT_TTL_MS) {
      return null;
    }
    if (expectedSessionId && parsed.session_id !== expectedSessionId) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function clearCheckoutReturnContext() {
  try { window.sessionStorage.removeItem(CHECKOUT_RETURN_STORAGE_KEY); } catch { /* noop */ }
}

/**
 * Deriva la URL de retorno a la que llevar al usuario tras confirmar la
 * compra. Prioridades (todas pasan por sanitizeInternalNext):
 *   1. `next` del query-string.
 *   2. `next` de sessionStorage (solo si session_id coincide).
 *   3. Fallback por `source_type` de la CheckoutSession.
 * Nunca devuelve una URL externa: el fallback es siempre una ruta interna.
 */
function resolveTargetUrl({ qsNext, ctx, session }) {
  const safeQs = sanitizeInternalNext(qsNext);
  if (safeQs) return safeQs;
  const safeCtx = sanitizeInternalNext(ctx && ctx.next);
  if (safeCtx) return safeCtx;
  const sourceType = session?.source_type;
  if (sourceType === 'plan') return '/dashboard';
  // content, content_download, event, download → sin `next` seguro no
  // podemos reconstruir el slug del evento; se ofrece el listado de partidos.
  return '/eventos';
}

function Outcome({ tone, Icon, title, message, primary, secondary, testId, customPrimary }) {
  const toneColors = {
    success: { bg: 'bg-[var(--ns-gold)]/15', text: 'text-[var(--ns-gold-700)]' },
    error: { bg: 'bg-[var(--ns-red)]/15', text: 'text-[var(--ns-red)]' },
    pending: { bg: 'bg-[var(--ns-bg-2)]', text: 'text-[var(--ns-text)]' },
  };
  const c = toneColors[tone] || toneColors.pending;
  return (
    <MainLayout>
      <section className="max-w-2xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <Card className="p-8 sm:p-10 rounded-3xl text-center bg-white border border-[var(--ns-border)]" data-testid={testId}>
          <div className={`h-16 w-16 rounded-3xl ${c.bg} ${c.text} mx-auto mb-5 inline-flex items-center justify-center`}>
            <Icon className="h-8 w-8" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[var(--ns-text)] mb-2">{title}</h1>
          <p className="text-sm sm:text-base text-[var(--ns-text-muted)] mb-6 leading-relaxed">{message}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {customPrimary ? customPrimary : (primary ? (
              <Button asChild className="h-11 rounded-xl bg-[var(--ns-gold)] hover:bg-[var(--ns-gold-700)] text-white font-semibold px-6">
                <Link to={primary.to} data-testid={`${testId}-primary`}>
                  {primary.icon ? <primary.icon className="h-4 w-4 mr-2" /> : null}
                  {primary.label}
                </Link>
              </Button>
            ) : null)}
            {secondary ? (
              <Button asChild variant="outline" className="h-11 rounded-xl border-[var(--ns-border)] text-[var(--ns-text)] px-6">
                <Link to={secondary.to} data-testid={`${testId}-secondary`}>{secondary.label}</Link>
              </Button>
            ) : null}
          </div>
        </Card>
      </section>
    </MainLayout>
  );
}

/**
 * Pantalla /checkout/exito.
 *
 * Regla crítica: la carga de esta pantalla NO concede acceso. El acceso
 * depende exclusivamente de las entidades (Payment/Purchase/Entitlement)
 * que el webhook crea al recibir la notificación server-to-server del TPV.
 *
 * Comportamiento:
 *   1. Recupera el session_id desde el query-string (flujo mock) o desde
 *      sessionStorage (flujo Redsys real).
 *   2. Consulta el estado real de la CheckoutSession en backend con poll.
 *   3. Si `confirmed` → redirige a la URL de origen (contenido/evento).
 *   4. Si `pending` tras el timeout → mensaje "confirmando" + volver manual.
 *   5. Si `failed/cancelled/expired` → navega a /checkout/error preservando
 *      el contexto de retorno.
 */
export function CheckoutSuccess() {
  const q = useQuery();
  const navigate = useNavigate();
  const qsNext = q.get('next') || '';
  const qsSessionId = q.get('session_id');

  const [ctx] = React.useState(() => readCheckoutReturnContext(qsSessionId));
  const sessionId = qsSessionId || ctx?.session_id || null;

  const [status, setStatus] = React.useState(sessionId ? 'checking' : 'no-session');
  // 'checking' | 'confirmed' | 'pending-timeout' | 'error-status' | 'no-session' | 'session-error'
  const [session, setSession] = React.useState(null);

  React.useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const startedAt = Date.now();

    const check = async () => {
      try {
        const data = await getCheckoutSession(sessionId);
        if (cancelled) return;
        setSession(data);
        const s = data?.status;
        if (s === 'confirmed') {
          setStatus('confirmed');
          return;
        }
        if (s === 'failed' || s === 'cancelled' || s === 'expired') {
          setStatus('error-status');
          return;
        }
        // pending: reintentar hasta agotar el presupuesto
        if (Date.now() - startedAt >= POLL_MAX_MS) {
          setStatus('pending-timeout');
          return;
        }
        setTimeout(() => { if (!cancelled) check(); }, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setSession(null);
        setStatus('session-error');
        toast.error(friendlyError(err));
      }
    };
    check();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Redirección automática cuando el backend confirma la sesión.
  // Punto 3: también aplica a descargas. Volvemos al contenido de origen;
  // desde allí el usuario dispara la descarga y el backend revalida el
  // entitlement (creado por el webhook) en /api/videos/{id}/download.
  React.useEffect(() => {
    if (status !== 'confirmed') return;
    const target = resolveTargetUrl({ qsNext, ctx, session });
    clearCheckoutReturnContext();
    // replace:true para que "Atrás" no vuelva a la pasarela.
    navigate(target, { replace: true });
  }, [status, qsNext, ctx, session, navigate]);

  // Redirección a /checkout/error cuando el estado terminal es negativo.
  // Estados terminales failed/cancelled/expired: limpiamos el contexto aquí
  // porque la sesión ya no puede evolucionar y no queremos que resida en
  // sessionStorage a la espera de ser purgada por unmount.
  React.useEffect(() => {
    if (status !== 'error-status') return;
    const qs = new URLSearchParams();
    if (sessionId) qs.set('session_id', sessionId);
    if (qsNext) qs.set('next', qsNext);
    clearCheckoutReturnContext();
    navigate(`/checkout/error${qs.toString() ? `?${qs.toString()}` : ''}`, { replace: true });
  }, [status, sessionId, qsNext, navigate]);

  // -------- Renderizado por estado --------

  if (status === 'checking') {
    return (
      <Outcome
        tone="pending" Icon={Loader2}
        title="Estamos confirmando tu pago…"
        message="No cierres esta pantalla. En cuanto la pasarela confirme la operación te llevaremos automáticamente al contenido comprado."
        testId="checkout-success-checking"
        customPrimary={
          <div className="flex items-center justify-center gap-2 text-sm text-[var(--ns-text-muted)]">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span data-testid="checkout-success-checking-status">Consultando estado…</span>
          </div>
        }
      />
    );
  }

  if (status === 'pending-timeout') {
    // El pago aún no está confirmado tras el timeout. NO se concede acceso.
    const target = resolveTargetUrl({ qsNext, ctx, session });
    return (
      <Outcome
        tone="pending" Icon={Clock}
        title="Estamos confirmando tu pago"
        message="La pasarela aún no nos ha comunicado el resultado. En cuanto se confirme, verás la compra activa en tu cuenta. Puedes volver al contenido y probar de nuevo el acceso en unos minutos."
        primary={{ label: 'Volver al contenido', to: target }}
        secondary={{ label: 'Mis compras', to: '/dashboard' }}
        testId="checkout-success-pending"
      />
    );
  }

  if (status === 'session-error') {
    return (
      <Outcome
        tone="error" Icon={AlertTriangle}
        title="No hemos podido verificar el pago"
        message="No hemos podido consultar el estado de la operación. Si el pago se ha realizado, aparecerá en tus compras en unos minutos."
        primary={{ label: 'Ir a mi cuenta', to: '/dashboard' }}
        secondary={{ label: 'Volver a partidos', to: '/eventos' }}
        testId="checkout-success-session-error"
      />
    );
  }

  if (status === 'no-session') {
    // Sin session_id no podemos verificar nada. No concedemos acceso; solo
    // ofrecemos rutas seguras al usuario. El acceso real se decide en el
    // paywall del contenido de destino según entitlements.
    return (
      <Outcome
        tone="pending" Icon={Clock}
        title="Sin información del pago"
        message="No hemos recibido información de la sesión de pago. Si acabas de completar una compra, revisa tus compras en unos minutos."
        primary={{ label: 'Ir a mi cuenta', to: '/dashboard' }}
        secondary={{ label: 'Volver a partidos', to: '/eventos' }}
        testId="checkout-success-no-session"
      />
    );
  }

  // status === 'confirmed'
  // Confirmado (incluye descargas por punto 3): la redirección al contenido
  // de origen se dispara en el useEffect superior. Renderizamos una
  // pantalla de transición mínima para evitar flicker.
  return (
    <Outcome
      tone="success" Icon={CheckCircle2}
      title="¡Pago confirmado!"
      message="Te llevamos al contenido comprado…"
      testId="checkout-success"
      customPrimary={
        <div className="flex items-center justify-center gap-2 text-sm text-[var(--ns-text-muted)]">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Redirigiendo…</span>
        </div>
      }
    />
  );
}

/**
 * Pantalla /checkout/error.
 * Consulta el estado real de la CheckoutSession (si hay session_id) para
 * clasificar el fallo, y ofrece "Volver al contenido" usando el `next`
 * original persistido durante el checkout.
 */
export function CheckoutError() {
  const q = useQuery();
  const qsNext = q.get('next') || '';
  const qsSessionId = q.get('session_id');
  // Punto 2: pasar qsSessionId para descartar ctx cuyo session_id no coincida.
  const [ctx] = React.useState(() => readCheckoutReturnContext(qsSessionId));
  const sessionId = qsSessionId || ctx?.session_id || null;
  const [session, setSession] = React.useState(null);

  React.useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getCheckoutSession(sessionId);
        if (!cancelled) setSession(data);
      } catch {
        // silencioso: si no podemos consultar, mostramos el error genérico.
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Limpiamos el contexto tras usar la información.
  React.useEffect(() => {
    return () => { clearCheckoutReturnContext(); };
  }, []);

  // Punto 1: sanear el `next` como ruta interna antes de usarlo como destino.
  const safeTarget = sanitizeInternalNext(qsNext) || sanitizeInternalNext(ctx?.next);
  const primary = safeTarget
    ? { label: 'Volver al contenido', to: safeTarget }
    : { label: 'Volver a planes', to: '/planes' };

  const message = session?.status === 'cancelled'
    ? 'Has cancelado el proceso de pago. Puedes intentarlo de nuevo cuando quieras.'
    : 'El pago no se completó. Si crees que es un error, inicia el proceso de nuevo o contacta con soporte.';

  return (
    <Outcome
      tone="error" Icon={AlertTriangle}
      title="No se pudo completar el pago"
      message={message}
      primary={primary}
      secondary={{ label: 'Volver a partidos', to: '/eventos' }}
      testId="checkout-error"
    />
  );
}

/**
 * Pantalla /checkout/pendiente. Reutiliza el mismo criterio: nunca concede
 * acceso, solo informa y ofrece volver al contenido de origen.
 */
export function CheckoutPending() {
  const q = useQuery();
  const qsNext = q.get('next') || '';
  const qsSessionId = q.get('session_id');
  // Punto 2: si hay session_id explícito, ctx debe coincidir con él.
  const [ctx] = React.useState(() => readCheckoutReturnContext(qsSessionId));
  // Punto 1: sanear el `next` como ruta interna antes de usarlo como destino.
  const safeTarget = sanitizeInternalNext(qsNext) || sanitizeInternalNext(ctx?.next);
  const primary = safeTarget
    ? { label: 'Volver al contenido', to: safeTarget }
    : { label: 'Volver al inicio', to: '/' };

  React.useEffect(() => {
    return () => { clearCheckoutReturnContext(); };
  }, []);

  return (
    <Outcome
      tone="pending" Icon={Clock}
      title="Pago pendiente"
      message="Hemos recibido tu intento pero aún no se ha confirmado. Te avisaremos en cuanto se procese."
      primary={primary}
      secondary={{ label: 'Mis compras', to: '/dashboard' }}
      testId="checkout-pending"
    />
  );
}
